import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { getCurrencySymbol } from "../lib/currency";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { DatePicker } from "./DatePicker";
import { toast } from "sonner";
import { Package, CreditCard, SplitSquareVertical, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export function AddPaymentModal({ open, onOpenChange, membership, packageName, onSuccess }) {
  const { organization } = useAuth();
  const currencySymbol = getCurrencySymbol(organization?.currency);

  const remaining = membership?.remainingAmount || 0;
  const isFullyPaid = remaining <= 0;

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    partialPayment: false,
    paidAmount: 0,
    partialPaymentDate: "",
    paymentMode: "Cash",
    transactionId: "",
    notes: "",
  });

  useEffect(() => {
    if (open) {
      setForm({
        partialPayment: false,
        paidAmount: remaining,
        partialPaymentDate: "",
        paymentMode: "Cash",
        transactionId: "",
        notes: "",
      });
    }
  }, [open, membership]);

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const effectiveAmount = form.partialPayment ? form.paidAmount : remaining;
  const afterPaymentRemaining = Math.max(0, remaining - effectiveAmount);

  const handleSubmit = async () => {
    if (isFullyPaid) return;
    if (!effectiveAmount || effectiveAmount <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/memberships/${membership.id}/payment`, {
        amount: effectiveAmount,
        paymentMode: form.paymentMode,
        transactionId: form.transactionId,
        notes: form.notes,
      });
      toast.success("Payment added successfully");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md p-0 flex flex-col max-h-[90vh]">
        <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
          <DialogTitle className="font-heading text-xl uppercase tracking-wide">Add Payment</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 relative">
          {loading && (
            <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[2px] flex items-center justify-center rounded-b-xl">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="font-mono">Processing...</span>
              </div>
            </div>
          )}

          <div className={`flex flex-col gap-5 ${loading ? "pointer-events-none select-none" : ""}`}>

            {/* Package Info */}
            <div className="flex items-center gap-3 p-4 bg-secondary/30 rounded-xl">
              <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <Package className="h-4 w-4" />
              </div>
              <div>
                <p className="font-semibold text-sm">{packageName || "-"}</p>
                <p className="text-xs text-muted-foreground font-mono">
                  {membership?.startDate?.slice(0, 10)} → {membership?.endDate?.slice(0, 10)}
                </p>
              </div>
              <Badge className="ml-auto text-[10px] font-mono uppercase border"
                style={{ background: "" }}
              >
                {membership?.status}
              </Badge>
            </div>

            {/* Payment Status */}
            {isFullyPaid ? (
              <div className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-emerald-500">Fully Paid</p>
                  <p className="text-xs text-muted-foreground">No outstanding balance on this membership</p>
                </div>
              </div>
            ) : (
              <Card className="bg-amber-500/5 border-amber-500/20">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                      Remaining Balance
                    </span>
                    <span className="text-xl font-black font-mono text-amber-500">
                      {currencySymbol}{remaining.toLocaleString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {!isFullyPaid && (
              <>
                {/* Partial Payment Toggle */}
                <Card className={`border transition-colors ${
                  form.partialPayment ? "border-amber-500/30 bg-amber-500/5" : "border-border"
                }`}>
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                          form.partialPayment ? "bg-amber-500/15 text-amber-500" : "bg-secondary text-muted-foreground"
                        }`}>
                          <SplitSquareVertical className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">Partial Payment</p>
                          <p className="text-xs text-muted-foreground">Pay a portion of the balance now</p>
                        </div>
                      </div>
                      <Switch
                        checked={form.partialPayment}
                        onCheckedChange={(v) => {
                          update("partialPayment", v);
                          update("paidAmount", v ? 0 : remaining);
                        }}
                        disabled={loading}
                      />
                    </div>

                    {form.partialPayment && (
                      <div className="space-y-3 pt-2">
                        <div className="space-y-2">
                          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                            Paid Amount ({currencySymbol})
                          </Label>
                          <Input
                            type="number"
                            value={form.paidAmount}
                            onChange={(e) => update("paidAmount", parseFloat(e.target.value) || 0)}
                            disabled={loading}
                          />
                        </div>
                        <div className="flex justify-between items-center py-2 px-3 bg-amber-500/10 rounded-lg">
                          <span className="text-sm text-amber-500 font-medium">Still Remaining</span>
                          <span className="text-lg font-bold font-mono text-amber-500">
                            {currencySymbol}{afterPaymentRemaining.toLocaleString()}
                          </span>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Due Date</Label>
                          <DatePicker
                            value={form.partialPaymentDate}
                            onChange={(v) => update("partialPaymentDate", v)}
                            placeholder="Next payment due date"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    )}

                    {!form.partialPayment && (
                      <div className="flex justify-between items-center py-2 px-3 bg-emerald-500/10 rounded-lg">
                        <span className="text-sm text-emerald-500 font-medium">Paying Full Balance</span>
                        <span className="text-lg font-bold font-mono text-emerald-500">
                          {currencySymbol}{remaining.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Payment Mode + Transaction ID */}
                <div className="grid grid-cols-2 gap-4 items-end">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <CreditCard className="h-3.5 w-3.5" /> Payment Mode
                    </Label>
                    <Select value={form.paymentMode} onValueChange={(v) => update("paymentMode", v)} disabled={loading}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Card">Card</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Transaction ID</Label>
                    <Input
                      value={form.transactionId}
                      onChange={(e) => update("transactionId", e.target.value)}
                      placeholder="Optional"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Notes</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    rows={2}
                    placeholder="Add any notes..."
                    disabled={loading}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-secondary/20 shrink-0">
          <Button
            className="w-full h-11 font-heading uppercase tracking-wider"
            onClick={handleSubmit}
            disabled={loading || isFullyPaid}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </span>
            ) : isFullyPaid ? (
              "Fully Paid"
            ) : (
              `Add Payment • ${currencySymbol}${effectiveAmount.toLocaleString()}`
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
